/**
 * anomaly_reason_dictionary_ru_v1 — versioned RU dictionary for the NEW anomaly
 * model (anomaly_v1) reason codes.
 *
 * Ported VERBATIM from the pipeline source of truth:
 *   full_training/data/raw_pipeline/06_anomaly/configs/anomaly_reason_dictionary_ru_v1.json
 * (generated 2026-07-14). Definitions trace to the actual anomaly reason-attribution
 * rules (source_feature + threshold), NOT inferred from names.
 *
 * These describe model-detected atypicality — NOT confirmed violations. Never mix
 * with legacy IF/Z/IQR method labels or Track A SHAP factors.
 */

export const ANOMALY_REASON_DICT_VERSION = 'anomaly_reason_dictionary_ru_v1'

/** Shown when anomaly_reasons is empty (approved neutral fallback — item 8). */
export const ANOMALY_NO_REASON_TEXT =
  'Модель считает кейс нетипичным относительно перевозок своей когорты. ' +
  'Детальная локальная причина для этого результата не сформирована.'

/** Always shown alongside reasons. */
export const ANOMALY_REASONS_DISCLAIMER =
  'Эти причины описывают обнаруженную моделью нетипичность и не являются подтверждёнными нарушениями.'

export interface AnomalyReasonEntry {
  title: string
  short: string
  template: string
  sourceFeature: string
  threshold: string
}

export const ANOMALY_REASON_DICT: Record<string, AnomalyReasonEntry> = {
  NEW_UNIT_TRANSITION: {
    title: 'Ранее для этой единицы такой переход не наблюдался',
    short: 'новый переход грузов для этого вагона/контейнера',
    template: 'Для данного вагона/контейнера сочетание «груз на входе → груз на выходе» ранее не встречалось.',
    sourceFeature: 'unit_new_transition_flag', threshold: 'flag = true',
  },
  NEW_UNIT_EXPORT_CARGO: {
    title: 'Для этой единицы экспорт данного груза является новым',
    short: 'юнит впервые вывозит такой груз',
    template: 'Данный вагон/контейнер ранее не вывозил груз этого типа.',
    sourceFeature: 'unit_new_export_cargo_flag', threshold: 'flag = true',
  },
  UNSEEN_GLOBAL_CARGO_TRANSITION: {
    title: 'Такой переход груза ранее не встречался в общей истории',
    short: 'такой переход грузов не встречался во всей истории',
    template: 'Сочетание «груз на входе → груз на выходе» не встречалось в исторических данных прошлого года ни у одного юнита.',
    sourceFeature: 'canonical_transition_unseen_flag', threshold: 'flag = true',
  },
  HIGH_GLOBAL_TRANSITION_SURPRISAL: {
    title: 'Переход редко встречается относительно общей истории груза',
    short: 'крайне редкий переход грузов',
    template: 'Переход между этими грузами исторически встречается значительно реже, чем ожидается для груза этого типа.',
    sourceFeature: 'canonical_transition_surprisal_proxy', threshold: 'percentile >= 90',
  },
  UNSEEN_CARGO_STATION_ROUTE: {
    title: 'Груз впервые идёт по этому станционному коридору',
    short: 'груз впервые идёт по этому станционному коридору',
    template: 'Данный груз ранее не перевозился по этому станционному коридору.',
    sourceFeature: 'cargo_station_corridor_unseen', threshold: 'flag = true',
  },
  HIGH_CARGO_ROUTE_SURPRISAL: {
    title: 'Маршрут нехарактерен для этого типа груза',
    short: 'крайне редкий маршрут для этого груза',
    template: 'Маршрут перевозки нехарактерен для данного типа груза по историческим данным.',
    sourceFeature: 'cargo_route_surprisal_proxy', threshold: 'percentile >= 90',
  },
  UNSEEN_PARTICIPANT_CHAIN: {
    title: 'Новая цепочка участников (отправитель → получатель)',
    short: 'новая цепочка отправитель→получатель',
    template: 'Цепочка участников (отправитель/получатель импорта → отправитель/получатель экспорта) ранее не встречалась.',
    sourceFeature: 'participant_chain_unseen_flag', threshold: 'flag = true',
  },
  UNSEEN_PAYER_TRANSITION: {
    title: 'Для данного плательщика такой переход ранее не наблюдался',
    short: 'новая связка плательщиков',
    template: 'Связка плательщиков импорта и экспорта ранее не встречалась.',
    sourceFeature: 'payer_transition_unseen_flag', threshold: 'flag = true',
  },
  UNUSUAL_INTERVAL_VS_UNIT_HISTORY: {
    title: 'Интервал нетипичен для истории этой единицы',
    short: 'интервал между операциями нетипичен для юнита',
    template: 'Интервал между прибытием и отправкой сильно отклоняется от исторического поведения этого вагона/контейнера.',
    sourceFeature: 'days_zscore_vs_unit_history', threshold: 'robust_z >= 3',
  },
  UNUSUAL_WEIGHT_VS_UNIT_HISTORY: {
    title: 'Вес отличается от обычного исторического профиля этой единицы',
    short: 'расхождение веса нетипично для юнита',
    template: 'Расхождение веса значительно отличается от исторического среднего для этого юнита.',
    sourceFeature: 'weight_diff_minus_unit_prior_avg', threshold: 'robust_z >= 3',
  },
  UNUSUAL_TRANSITION_INTERVAL: {
    title: 'Интервал нетипичен для этого перехода грузов',
    short: 'интервал нетипичен для этого перехода грузов',
    template: 'Интервал между операциями отклоняется от типичного для данного перехода грузов.',
    sourceFeature: 'transition_days_minus_prior_avg', threshold: 'robust_z >= 3',
  },
  UNUSUAL_ROUTE_INTERVAL: {
    title: 'Интервал нетипичен для этого станционного коридора',
    short: 'интервал нетипичен для маршрута',
    template: 'Интервал между операциями отклоняется от типичного для данного станционного коридора.',
    sourceFeature: 'route_days_minus_prior_avg', threshold: 'robust_z >= 3',
  },
  UNIT_ACTIVITY_BURST: {
    title: 'Всплеск активности вагона/контейнера',
    short: 'всплеск активности вагона/контейнера',
    template: 'Активность юнита за последние 7 дней резко превышает его фоновую активность за 90 дней.',
    sourceFeature: 'unit_burst_ratio_7_to_90', threshold: 'robust_z >= 3',
  },
  TRANSITION_ACTIVITY_BURST: {
    title: 'Обнаружен всплеск активности аналогичного перехода',
    short: 'всплеск частоты этого перехода грузов',
    template: 'Частота данного перехода грузов за 30 дней резко превышает фоновый уровень за 90 дней.',
    sourceFeature: 'transition_burst_ratio_30_to_90', threshold: 'robust_z >= 3',
  },
  CARGO_CODE_STRONGLY_CHANGED: {
    title: 'Код груза существенно сменился на выходе',
    short: 'код груза полностью сменился',
    template: 'Код груза на выходе существенно отличается от кода на входе (смена товарной позиции). Для candidate-пар это частое явление, признак имеет низкий вес.',
    sourceFeature: 'cargo_change_severity', threshold: 'severity >= 3',
  },
  TRANSFER_PATTERN: {
    title: 'Паттерн перегруза между разными единицами',
    short: 'перегруз между разными юнитами',
    template: 'Пара соответствует паттерну перегруза между разными вагонами/контейнерами.',
    sourceFeature: 'candidate_family', threshold: 'candidate_family = transfer',
  },
  LOW_PAIRING_CONFIDENCE: {
    title: 'Низкая уверенность сопоставления пары',
    short: 'низкая уверенность в самой паре',
    template: 'Сопоставление импорт↔экспорт неоднозначно: есть альтернативные кандидаты. Интерпретируйте аномалию с осторожностью.',
    sourceFeature: 'pairing_confidence_score', threshold: '< 0.35',
  },
  DATA_QUALITY_WARNING: {
    title: 'Возможны проблемы качества данных',
    short: 'проблемы качества данных',
    template: 'В записи есть проблемы качества данных (дубликаты, пропуски, fallback-даты). Аномальность может объясняться качеством данных.',
    sourceFeature: 'data_quality_confidence_score', threshold: '< 0.7',
  },
  NO_PRIOR_HISTORY: {
    title: 'Нет исторических данных за прошлый год',
    short: 'нет истории за прошлый год',
    template: 'Для пары нет данных за предыдущий год: исторические компоненты оценки недоступны, скор опирается на локальные признаки.',
    sourceFeature: 'history_available_prior_year', threshold: 'flag = false',
  },
}

export function getAnomalyReason(code: string): AnomalyReasonEntry | null {
  return ANOMALY_REASON_DICT[code] ?? null
}

/** Neutral title for an unknown code — never fabricate a specific meaning. */
export function anomalyReasonTitle(code: string): string {
  return ANOMALY_REASON_DICT[code]?.title ?? 'Модельный признак нетипичности'
}
